/**
 * Reusable file uploader for admin pages.
 *
 * Usage:
 *   <ImageUploader value={imgUrl} onChange={(url) => setImgUrl(url)} />
 *   <ImageUploader value={url} onChange={setUrl} accept="video/*" maxMB={80} kind="video" />
 *
 * Renders a preview + an "Upload" button + a text URL field (so admins can paste
 * an external URL too). Uploads go to POST /api/admin/upload which saves under
 * /uploads/yyyy/mm/<uuid>.<ext> and returns { url }.
 */
import { useRef, useState } from "react";
import { Upload, X, Loader2, Image as ImageIcon, Film } from "lucide-react";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function absolutize(url) {
    if (!url) return "";
    if (url.startsWith("http") || url.startsWith("data:")) return url;
    if (url.startsWith("/api/uploads") || url.startsWith("/uploads")) return `${BACKEND_URL}${url}`;
    return url;
}

export default function ImageUploader({
    value,
    onChange,
    label = "Image",
    accept = "image/*",
    kind = "image", // 'image' or 'video'
    maxMB = 12,
    className = "",
    compact = false,
}) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");

    const pick = () => inputRef.current?.click();

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > maxMB * 1024 * 1024) {
            setError(`File too large (max ${maxMB} MB)`);
            return;
        }
        setError("");
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange?.(data.url);
        } catch (err) {
            setError(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const clear = () => onChange?.("");

    const preview = absolutize(value);
    const isVideo = kind === "video" || (preview && /\.(mp4|webm|mov)$/i.test(preview));

    return (
        <div className={`space-y-2 ${className}`} data-testid="image-uploader">
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                onChange={handleFile}
                className="hidden"
                data-testid="image-uploader-input"
            />

            {!compact && (
                <label className="block text-[11px] font-mono-ui tracking-[0.15em] uppercase text-[#857C72]">
                    {label}
                </label>
            )}

            {/* Preview */}
            {preview ? (
                <div className="relative group rounded-2xl overflow-hidden border border-[#E7DED2] bg-[#FFFCF7]">
                    {isVideo ? (
                        <video src={preview} className="w-full h-40 object-cover" controls />
                    ) : (
                        <img src={preview} alt="" className="w-full h-40 object-cover" />
                    )}
                    <button
                        type="button"
                        onClick={clear}
                        className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Remove"
                        data-testid="image-uploader-clear"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={pick}
                    disabled={uploading}
                    className="w-full h-40 rounded-2xl border-2 border-dashed border-[#E7DED2] hover:border-[#1B1B1F] bg-[#FFFCF7] text-[#857C72] flex flex-col items-center justify-center gap-2 transition-colors disabled:opacity-60"
                    data-testid="image-uploader-dropzone"
                >
                    {uploading ? (
                        <>
                            <Loader2 className="h-6 w-6 animate-spin" />
                            <span className="text-xs font-mono-ui">Uploading…</span>
                        </>
                    ) : (
                        <>
                            {isVideo ? <Film className="h-7 w-7" /> : <ImageIcon className="h-7 w-7" />}
                            <span className="text-xs font-mono-ui">Click to upload (max {maxMB} MB)</span>
                        </>
                    )}
                </button>
            )}

            {/* Action row */}
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={pick}
                    disabled={uploading}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E7DED2] hover:bg-[#FFFCF7] text-xs font-mono-ui disabled:opacity-60"
                    data-testid="image-uploader-upload-btn"
                >
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    {preview ? "Replace" : "Upload"}
                </button>
                <input
                    type="text"
                    value={value || ""}
                    onChange={(e) => onChange?.(e.target.value)}
                    placeholder="or paste URL / path…"
                    className="flex-1 px-3 py-1.5 rounded-full bg-white border border-[#E7DED2] text-xs focus:outline-none focus:ring-2 focus:ring-[#1B1B1F]/10"
                    data-testid="image-uploader-url-input"
                />
            </div>

            {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
    );
}
